import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Spinner } from "react-bootstrap";

import UserAuthService from "../services/UserAuthService";
import ApiService from "../services/ApiService";
import { ApiError } from "../errors";

type BackendVerificationState = "pending" | "success" | "error" | "not_started";

interface ProtectedRouteProps {
    element: React.ReactElement;
    authService: UserAuthService;
    apiService: ApiService;
    children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, authService, apiService, children }) => {
    const { t } = useTranslation(["common", "auth"]);
    const location = useLocation();
    const navigate = useNavigate();
    const [isInitialized, setIsInitialized] = useState(authService.isInitialized());
    const [isAuthenticated, setIsAuthenticated] = useState(authService.isLoggedIn());
    const [backendVerification, setBackendVerification] = useState<BackendVerificationState>("not_started");
    const [verificationError, setVerificationError] = useState<ApiError | null>(null);

    // Listen for initialization and auth state changes
    useEffect(() => {
        const unsubscribeInit = authService.onInitializationChanged((initialized) => {
            setIsInitialized(initialized);
            if (initialized) {
                setIsAuthenticated(authService.isLoggedIn());
            }
        });

        const unsubscribeAuth = authService.onAuthStateChanged((user) => {
            const authenticated = !!user;
            setIsAuthenticated(authenticated);

            if (!authenticated && isInitialized) {
                // User lost authentication while on protected route - redirect to login
                navigate("/login", {
                    state: { from: location },
                    replace: true,
                });
            }
        });

        // Cleanup listeners when component unmounts
        return () => {
            unsubscribeInit();
            unsubscribeAuth();
        };
    }, [authService, navigate, location, isInitialized]);

    // Backend verification when user is authenticated
    useEffect(() => {
        if (isAuthenticated && isInitialized && backendVerification === "not_started") {
            setBackendVerification("pending");
            setVerificationError(null);

            apiService
                .verifyAuthentication()
                .then(() => {
                    setBackendVerification("success");
                })
                .catch((error) => {
                    setBackendVerification("error");
                    setVerificationError(error);
                });
        }

        // Reset backend verification when user logs out
        if (!isAuthenticated) {
            setBackendVerification("not_started");
            setVerificationError(null);
        }
    }, [isAuthenticated, isInitialized, backendVerification, apiService]);

    // Show loading while auth service is initializing
    if (!isInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: "50vh" }}>
                <Spinner animation="border" role="status" className="protected-route-spinner">
                    <span className="visually-hidden">{t("common:loading")}</span>
                </Spinner>
                <p className="m-3">{t("auth:oauth.processing")}</p>
            </div>
        );
    }

    // Redirect to login if not authenticated (only after initialization is complete)
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Show loading while backend verification is pending
    if (backendVerification === "pending") {
        return (
            <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: "50vh" }}>
                <Spinner animation="border" role="status" className="protected-route-spinner">
                    <span className="visually-hidden">{t("common:loading")}</span>
                </Spinner>
                <p className="m-3">{t("auth:verification.processing", "Verifying authentication...")}</p>
            </div>
        );
    }

    // Show error if backend verification failed
    if (backendVerification === "error" && verificationError) {
        const handleLogout = () => {
            authService.logout();
        };

        // Map error codes to specific messages
        const getErrorMessage = () => {
            if (verificationError.errorCode === 40336) {
                // ERROR_CODE_AUTHENTICATION_KEYCLOAK_SUPERADMIN_ERROR
                return t("auth:errors.superadminNotAllowed", "Superadmins cannot access {{productName}}");
            } else if (verificationError.errorCode === 4035) {
                // ERROR_CODE_AUTHENTICATION_UNAUTHORIZED_ACCOUNT_ID_ERROR
                return t("auth:errors.accountUnauthorized", "Your account is not authorized to use this application");
            } else {
                return t("auth:errors.backendAuthFailed", "Authentication verification failed");
            }
        };

        return (
            <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: "50vh" }}>
                <Alert variant="danger" className="text-center">
                    <Alert.Heading>{t("auth:errors.authenticationRequired", "Authentication Required")}</Alert.Heading>
                    <p>{getErrorMessage()}</p>
                    <hr />
                    <Button variant="primary" onClick={handleLogout}>
                        {t("auth:actions.logout", "Logout")}
                    </Button>
                </Alert>
            </div>
        );
    }

    // Only show protected content if backend verification succeeded
    if (backendVerification === "success") {
        return element;
    }

    // Fallback - should not reach here
    return (
        <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: "50vh" }}>
            <Spinner animation="border" role="status" className="protected-route-spinner">
                <span className="visually-hidden">{t("common:loading")}</span>
            </Spinner>
        </div>
    );
};

export default ProtectedRoute;
